// src/design/DesignCenterAIAssist.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL;
const THINK_DELAY = 3000; // ms before each scripted AI message appears

// ─── Section-specific AI scripts ─────────────────────────────────────────────
// Each section has: opening message, 4 questions with field keys + acknowledgments
const SECTION_SCRIPTS = {
  how_things_work: {
    opening: `Let's break down how something in your world works — a process, a system, a product, or a decision your customers face. When people understand what goes into it, they trust the expert who explained it. What do you want to demystify for your audience?`,
  },
  how_to_tips: {
    opening: `Let's give your audience something they can actually use — a tip, a shortcut, a maintenance trick, or a "wish I knew this sooner" moment. The best tips make people save your post and come back to it. What's something your customers should know how to do?`,
  },
  problems_solutions: {
    opening: `Every customer has a pain point they're dealing with — something broken, something they're worried about, or something costing them money. When you name the problem and show the fix, you become the person they call. What's a problem your customers run into that you solve?`,
  },
  advice_insights: {
    opening: `You've seen things in your industry that most people haven't. Maybe it's a common mistake people make, a misconception that costs them, or a lesson you've learned the hard way. That kind of insight builds real trust. What's a piece of advice you wish every customer knew?`,
  },
  specials_discounts: {
    opening: `Let's make your offer impossible to scroll past. Whether it's a discount, a bundle, a free consultation, or a seasonal deal — the key is making people feel like they'd be crazy to miss it. What's the offer and why should someone act on it right now?`,
  },
  stories_experiences: {
    opening: `People connect with stories more than sales pitches. A job that went sideways and you saved it, a customer who thanked you, a moment that reminded you why you do this — those are the posts people share. What's a story from your business worth telling?`,
  },
  community_relations: {
    opening: `People want to support businesses that show up for their community — sponsoring the little league, volunteering after a storm, partnering with a local charity, or just being the business everyone knows by name. What's something you're doing in your community that people should see?`,
  },
  humor_personality: {
    opening: `The posts that get the most engagement aren't always about your services — they're the ones that make people laugh, nod, or tag a friend. A workplace moment, an industry joke, a "you had to be there" story. What's something funny, relatable, or real about your day-to-day?`,
  },
};

// Fallback for unknown section keys
const DEFAULT_SCRIPT = SECTION_SCRIPTS.how_things_work;

/**
 * Typing indicator — animated dots
 */
function TypingIndicator() {
  return (
    <div className="mr-auto bg-gray-100 rounded-lg px-3 py-2 flex items-center gap-1">
      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
  );
}

/**
 * AI Assist chat — collects a brief through section-specific questions, then calls
 * the real OpenAI endpoint to generate a headline + platform captions.
 *
 * REVISION STRATEGY:
 * - ONE endpoint: /api/designcenter/ai/generate-captions
 * - First call sends { brief, category } → full generation
 * - Every subsequent call sends { existing, revision } → cheap revision
 * - cachedResultRef stores the last result and is NEVER cleared.
 */
export default function DesignCenterAIAssist({
  selected,
  activeTabLabel,
  sectionKey,
  onCaptionsGenerated,
  onBriefComplete,
  inspirationAnswer,
  inspirationDismissed,
  inspirationLoading,
  inspirationIdeas,
  onReopenInspiration,
}) {
  // Get the script for the current section
  const script = SECTION_SCRIPTS[sectionKey] || DEFAULT_SCRIPT;

  // Conversation state — reset when section changes
  const [messages, setMessages] = useState([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [brief, setBrief] = useState({});
  const cachedResultRef = useRef(null);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const endRef = useRef(null);
  const thinkTimerRef = useRef(null);
  const prevSectionRef = useRef(sectionKey);
  const inputRef = useRef(null);

  // Reset conversation when section changes
  useEffect(() => {
    if (sectionKey !== prevSectionRef.current) {
      prevSectionRef.current = sectionKey;
      if (thinkTimerRef.current) clearTimeout(thinkTimerRef.current);
      const newScript = SECTION_SCRIPTS[sectionKey] || DEFAULT_SCRIPT;
      setMessages([{
        id: crypto.randomUUID(),
        role: 'ai',
        text: newScript.opening,
      }]);
      setQuestionIndex(0);
      setBrief({});
      cachedResultRef.current = null;
      setInput('');
      setIsThinking(false);
      initialSigRef.current = null;
    }
  }, [sectionKey]);

  // Initialize on first mount
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        id: crypto.randomUUID(),
        role: 'ai',
        text: script.opening,
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (thinkTimerRef.current) clearTimeout(thinkTimerRef.current);
    };
  }, []);

  // Keep a stable "selected signature"
  const selectedSig = useMemo(() => {
    if (!selected?.filename) return '';
    return `${selected.filename}`;
  }, [selected?.filename]);

  // Track whether user has made at least one image selection (skip initial auto-select)
  const initialSigRef = useRef(null);

  // When the selected image changes, acknowledge it (but not on first load)
  useEffect(() => {
    if (!selectedSig) return;

    // First image selected = initial auto-select, just record it
    if (initialSigRef.current === null) {
      initialSigRef.current = selectedSig;
      return;
    }

    // Same image as initial = no-op
    if (selectedSig === initialSigRef.current && !cachedResultRef.current) return;

    setMessages((prev) => {
      const notice = `Image updated. Your draft stays intact.`;
      const last = prev[prev.length - 1];

      if (last?.role === 'ai' && last?.meta === 'image-change') {
        return [
          ...prev.slice(0, -1),
          { ...last, id: crypto.randomUUID(), text: notice, sig: selectedSig },
        ];
      }

      return [
        ...prev,
        { id: crypto.randomUUID(), role: 'ai', text: notice, meta: 'image-change', sig: selectedSig },
      ];
    });
  }, [selectedSig]);

  // Auto-scroll
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length, isThinking]);

  // Auto-focus input when AI finishes thinking
  useEffect(() => {
    if (!isThinking) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isThinking]);

  // Fetch AI-generated follow-up question based on user's first answer
  const fetchFollowUp = useCallback(async (userAnswer) => {
    try {
      const res = await fetch(`${API_URL}/api/designcenter/ai/follow-up`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: activeTabLabel || 'general', userAnswer }),
      });
      const data = await res.json();
      return data.ok ? data.followUp : null;
    } catch {
      return null;
    }
  }, [activeTabLabel]);

  // Auto-answer first question when an inspiration idea was chosen
  const inspirationUsedRef = useRef(false);
  useEffect(() => {
    if (!inspirationAnswer || inspirationUsedRef.current) return;
    if (questionIndex !== 0 || messages.length < 1) return;
    const timer = setTimeout(() => {
      if (inspirationUsedRef.current) return;
      inspirationUsedRef.current = true;

      // Show the answer as a user message
      setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'user', text: inspirationAnswer }]);
      setBrief((b) => ({ ...b, goal: inspirationAnswer }));
      setQuestionIndex(1);

      // Fetch AI follow-up
      setIsThinking(true);
      (async () => {
        const followUp = await fetchFollowUp(inspirationAnswer);
        setIsThinking(false);
        setMessages((prev) => [...prev, {
          id: crypto.randomUUID(),
          role: 'ai',
          text: followUp || `Got it — what do you want people to do or feel when they see this?`,
        }]);
      })();
    }, 500);
    return () => clearTimeout(timer);
  }, [inspirationAnswer, questionIndex, messages.length, fetchFollowUp]);

  const pushMessage = useCallback((role, text, extra) => {
    setMessages((prev) => [...prev, { id: crypto.randomUUID(), role, text, ...extra }]);
  }, []);

  // Delayed scripted AI response
  const sendDelayedAI = useCallback((acknowledgment, followUp) => {
    setIsThinking(true);

    thinkTimerRef.current = setTimeout(() => {
      pushMessage('ai', acknowledgment);
      setIsThinking(false);

      if (followUp) {
        setTimeout(() => {
          setIsThinking(true);
          thinkTimerRef.current = setTimeout(() => {
            pushMessage('ai', followUp);
            setIsThinking(false);
          }, THINK_DELAY);
        }, 200);
      }
    }, THINK_DELAY);
  }, [pushMessage]);

  // Show generated results in chat
  const showResultsInChat = useCallback((data, isRevision) => {
    pushMessage('ai', isRevision ? `Updated headline:` : `Here's your headline for the image:`);

    setTimeout(() => {
      pushMessage('ai', `"${data.headline}"`, { meta: 'headline' });
    }, 300);

    setTimeout(() => {
      const captionLines = [];
      if (data.captions?.facebook) captionLines.push(`Facebook: ${data.captions.facebook}`);
      if (data.captions?.instagram) captionLines.push(`Instagram: ${data.captions.instagram}`);
      if (data.captions?.linkedin) captionLines.push(`LinkedIn: ${data.captions.linkedin}`);
      if (data.captions?.youtube) captionLines.push(`YouTube: ${data.captions.youtube}`);
      if (data.captions?.twitter) captionLines.push(`Twitter/X: ${data.captions.twitter}`);
      if (data.captions?.tiktok) captionLines.push(`TikTok: ${data.captions.tiktok}`);

      pushMessage('ai', captionLines.join('\n\n'), { meta: 'captions' });
    }, 600);

    setTimeout(() => {
      pushMessage('ai', `Want to adjust anything? Tell me what to change, or click "Preview by Social Platform" when it looks right.`);
      setIsThinking(false);
    }, 900);
  }, [pushMessage]);

  /**
   * callAI — ONE function for ALL AI calls.
   */
  const callAI = useCallback(async (payload) => {
    setIsThinking(true);

    try {
      const res = await fetch(`${API_URL}/api/designcenter/ai/generate-captions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        pushMessage('ai', `Something went wrong. ${data.error || 'Please try again.'}`);
        setIsThinking(false);
        return;
      }

      const isRevision = !!payload.existing;

      // Update cache — always
      cachedResultRef.current = { headline: data.headline, captions: data.captions };

      // Pass up to parent
      if (onCaptionsGenerated) {
        onCaptionsGenerated({ headline: data.headline, captions: data.captions });
      }

      showResultsInChat(data, isRevision);

    } catch (err) {
      console.error('[DesignCenterAIAssist] callAI error:', err);
      pushMessage('ai', `Network error — couldn't reach the AI service. Please try again.`);
      setIsThinking(false);
    }
  }, [onCaptionsGenerated, pushMessage, showResultsInChat]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || isThinking) return;

    pushMessage('user', text);
    setInput('');

    // ━━━ REVISION — if we have cached data, ALWAYS use cheap revision ━━━
    if (cachedResultRef.current) {
      pushMessage('ai', `Updating captions...`);
      callAI({
        existing: cachedResultRef.current,
        revision: text,
      });
      return;
    }

    // ━━━ Step 1: User answered the opening question ━━━
    if (questionIndex === 0) {
      setBrief((b) => ({ ...b, goal: text }));
      setQuestionIndex(1);

      // Call AI for a natural follow-up question
      setIsThinking(true);
      (async () => {
        const followUp = await fetchFollowUp(text);
        setIsThinking(false);
        if (followUp) {
          pushMessage('ai', followUp);
        } else {
          // Fallback if AI call fails
          pushMessage('ai', `Got it — what do you want people to do or feel when they see this?`);
        }
      })();
      return;
    }

    // ━━━ Step 2: User answered the follow-up — generate captions ━━━
    if (questionIndex === 1) {
      const completedBrief = { ...brief, goal: brief.goal, context: text };
      setBrief(completedBrief);
      setQuestionIndex(2);

      if (onBriefComplete) onBriefComplete(completedBrief);

      pushMessage('ai', `Generating your headline and captions now...`);
      callAI({ brief: completedBrief, category: activeTabLabel || 'general' });
      return;
    }

    // Fallback
    pushMessage('ai', `Tell me what to change, or click "Preview by Social Platform" when it looks right.`);
  };

  return (
    <div className="flex flex-col h-full min-h-0">
        {/* Chat window */}
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden flex flex-col flex-1 min-h-0">
        {/* Messages (scrolls) */}
        <div className="flex-1 min-h-0 overflow-auto overscroll-contain p-2 space-y-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
            {/* Lightbulb — reopen inspiration */}
            {inspirationDismissed && (inspirationLoading || (inspirationIdeas && inspirationIdeas.length > 0)) && (
              <button
                type="button"
                onClick={onReopenInspiration}
                className="eh-design-nodrag flex items-center gap-2 w-full px-2 py-1.5 rounded-lg hover:bg-yellow-50 transition-colors"
                title="Show inspiration ideas"
              >
                <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#00aef7" className="shrink-0">
                  <path d="M423.5-103.5Q400-127 400-160h160q0 33-23.5 56.5T480-80q-33 0-56.5-23.5ZM320-200v-80h320v80H320Zm10-120q-69-41-109.5-110T180-580q0-125 87.5-212.5T480-880q125 0 212.5 87.5T780-580q0 81-40.5 150T630-320H330Zm24-80h252q45-32 69.5-79T700-580q0-92-64-156t-156-64q-92 0-156 64t-64 156q0 54 24.5 101t69.5 79Zm126 0Z" />
                </svg>
                <span className="text-[11px] font-semibold text-[#00aef7]">Need Inspiration?</span>
              </button>
            )}
            {messages.map((m) => (
            <div
                key={m.id}
                className={`max-w-[92%] rounded-lg px-2 py-1 text-[11px] leading-snug whitespace-pre-wrap break-words ${
                m.role === 'user'
                    ? 'ml-auto bg-[#00aef7] text-white'
                    : m.meta === 'headline'
                    ? 'mr-auto bg-yellow-50 border border-yellow-200 text-gray-900 font-semibold'
                    : m.meta === 'captions'
                    ? 'mr-auto bg-blue-50 border border-blue-200 text-gray-700'
                    : 'mr-auto bg-gray-100 text-gray-700'
                }`}
            >
                {m.text}
            </div>
            ))}
            {isThinking && <TypingIndicator />}
            <div ref={endRef} />
        </div>

        {/* Input (always pinned) */}
        <div className="shrink-0 border-t border-gray-200 p-2 flex gap-1 bg-white">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSend();
            }}
            placeholder={isThinking ? '' : 'Type your answer…'}
            disabled={isThinking}
            className="eh-design-nodrag flex-1 min-w-0 px-2 py-1 rounded border border-gray-200 text-xs focus:outline-none focus:border-[#00aef7] disabled:bg-gray-50 disabled:text-gray-400"
          />

            <button
            type="button"
            onClick={handleSend}
            disabled={isThinking}
            className="eh-design-nodrag shrink-0 w-6 h-6 rounded-full bg-[#00aef7] hover:bg-[#0097d6] disabled:bg-gray-300 flex items-center justify-center"
            aria-label="Send"
            title="Send"
            >
            <svg
                viewBox="0 0 24 24"
                className="w-5 h-5"
                fill="none"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
            >
                <path d="M12 19V5" />
                <path d="M5 12l7-7 7 7" />
            </svg>
            </button>

        </div>
      </div>
    </div>
  );
}
